import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import { useContracts } from "@/features/contracts/hooks/use-contracts"
import { useCooperatives } from "@/features/cooperatives/hooks/use-cooperatives"
import { OrdersKpiStrip } from "@/features/orders/components/orders-kpi-strip"
import {
  OrdersSearchFilters,
  type OrdersFiltersValue,
} from "@/features/orders/components/orders-search-filters"
import { OrdersTable } from "@/features/orders/components/orders-table"
import { useOrders } from "@/features/orders/hooks/use-orders"
import { bucketForOrderStatus } from "@/features/orders/lib/order-status"

export function OrdersPage() {
  const { t } = useTranslation("orders")
  const { data: orders, isLoading } = useOrders(50)
  const { data: cooperatives } = useCooperatives()
  const { data: contracts } = useContracts()

  const [filters, setFilters] = useState<OrdersFiltersValue>({
    search: "",
    status: "all",
    cooperative: "all",
  })

  const cooperativeOptions = useMemo(
    () =>
      (cooperatives ?? [])
        .map((c) => ({ id: String(c.id), name: c.name ?? `#${c.id}` }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [cooperatives],
  )

  const contractLabelsById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const c of contracts ?? []) {
      map[String(c.id)] = c.contractNumber ?? `#${c.id}`
    }
    return map
  }, [contracts])

  const filtered = useMemo(() => {
    const lower = filters.search.toLowerCase().trim()
    return (orders ?? []).filter((o) => {
      const cooperativeName = (o.groupId?.name ?? "").toLowerCase()
      const matchSearch =
        !lower ||
        o.orderNumber?.toLowerCase().includes(lower) ||
        cooperativeName.includes(lower) ||
        (o.customerId?.clientName ?? "").toLowerCase().includes(lower)
      const matchStatus =
        filters.status === "all" ||
        bucketForOrderStatus(o.orderStatus) === filters.status
      // groupId on the list response is { name } only — no id at the
      // list-item level. Match on cooperative name so the filter works
      // without an extra fetch.
      const matchCooperative =
        filters.cooperative === "all" ||
        cooperativeOptions.find((c) => c.id === filters.cooperative)?.name ===
          o.groupId?.name
      return matchSearch && matchStatus && matchCooperative
    })
  }, [orders, filters, cooperativeOptions])

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
        meta={
          orders ? (
            <span className="text-muted-foreground text-sm">
              · {t("search.resultsCount", { count: filtered.length })}
            </span>
          ) : null
        }
      />

      <OrdersKpiStrip orders={orders ?? []} />

      <OrdersSearchFilters
        value={filters}
        onChange={setFilters}
        cooperativeOptions={cooperativeOptions}
      />

      {isLoading ? (
        <LoadingState />
      ) : (
        <OrdersTable orders={filtered} contractLabelsById={contractLabelsById} />
      )}
    </div>
  )
}
